import { Box, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import { File, PanelLeftClose, PanelLeftOpen } from 'lucide-react';
import React, { useState } from 'react';
import { useProjectStore } from '../../stores/projectStore';
import ScrollBox from '../ui/ScrollBox';

const SIDEBAR_WIDTH = 240;
const COLLAPSED_WIDTH = 36;

/**
 * FileBrowserSidebar — fixed collapsible sidebar for the Analysis screen.
 * Shows the flat file list from the current project.
 * Outside of dockview — sits to the left of the dockview container.
 */
const FileBrowserSidebar = ({ onOpenFile }) => {
  const [collapsed, setCollapsed] = useState(false);
  const projectFiles = useProjectStore((state) => state.projectFiles);
  const currentProjectId = useProjectStore((state) => state.currentProjectId);
  const currentProject = useProjectStore((state) =>
    state.projects.find((p) => p.project_id === state.currentProjectId),
  );

  if (collapsed) {
    return (
      <Box
        sx={{
          width: COLLAPSED_WIDTH,
          minWidth: COLLAPSED_WIDTH,
          height: '100%',
          borderRight: '1px solid rgba(0,0,0,0.1)',
          bgcolor: '#E9ECED',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          pt: 0.5,
        }}
      >
        <Tooltip title="Show files" placement="right">
          <IconButton size="small" onClick={() => setCollapsed(false)}>
            <PanelLeftOpen size={16} strokeWidth={1.5} />
          </IconButton>
        </Tooltip>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        width: SIDEBAR_WIDTH,
        minWidth: SIDEBAR_WIDTH,
        height: '100%',
        borderRight: '1px solid rgba(0,0,0,0.1)',
        bgcolor: '#E9ECED',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 1,
          py: 0.5,
          borderBottom: '1px solid rgba(0,0,0,0.1)',
        }}
      >
        <Typography
          variant="caption"
          sx={{
            fontWeight: 600,
            color: '#394b57',
            fontSize: 11,
            textTransform: 'uppercase',
            letterSpacing: 0.5,
          }}
        >
          {currentProject?.name || 'Files'}
        </Typography>
        <IconButton size="small" onClick={() => setCollapsed(true)}>
          <PanelLeftClose size={14} strokeWidth={1.5} />
        </IconButton>
      </Box>

      {/* File list */}
      <ScrollBox sx={{ flex: 1 }}>
        {!currentProjectId && (
          <Box sx={{ p: 2, color: '#7d8d97', fontSize: 12 }}>
            Select a project in the Project Explorer to see files.
          </Box>
        )}
        {currentProjectId && projectFiles.length === 0 && (
          <Box sx={{ p: 2, color: '#7d8d97', fontSize: 12 }}>No files in this project.</Box>
        )}
        <Stack spacing={0}>
          {projectFiles.map((f) => (
            <Box
              key={f.filename}
              onDoubleClick={() => onOpenFile?.(currentProjectId, f.filename)}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.75,
                px: 1,
                userSelect: 'none',
                py: 0.5,
                cursor: 'pointer',
                '&:hover': { bgcolor: 'rgba(0,0,0,0.05)' },
                borderRadius: 0.5,
                mx: 0.5,
              }}
            >
              <File size={13} strokeWidth={1.5} color="#7d8d97" />
              <Typography
                variant="body2"
                sx={{
                  fontSize: 12,
                  color: '#394b57',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
                title={f.filename}
              >
                {f.filename}
              </Typography>
            </Box>
          ))}
        </Stack>
      </ScrollBox>
    </Box>
  );
};

export default FileBrowserSidebar;
